<template>
  <div class="card mt-4" v-if="started || !readonly">
    <div class="card-body">
      <div
        class="d-flex align-items-center justify-content-around flex-wrap"
        v-if="!readonly"
      >
        <button
          type="button"
          class="btn"
          style="background: #92d050; color: #fff;"
          :disabled="busy || running || ended"
          @click="start()"
        >
          Start Road Test Timer
        </button>

        <stopwatch-icon :time="display" :size="110"></stopwatch-icon>

        <button
          type="button"
          class="btn"
          style="background: #c00000; color: #fff;"
          :disabled="busy || !running"
          @click="stop()"
        >
          Stop Road Test Timer
        </button>
      </div>

      <p class="text-center mb-0 mt-2" v-if="ended">
        Road test completed in {{ display }}
      </p>
      <p class="text-center mb-0 mt-2" v-else-if="running">
        Road test in progress
      </p>
      <p class="text-center mb-0" v-if="readonly && started">
        Road test time: {{ display }}
      </p>
    </div>
  </div>
</template>

<script>
import StopwatchIcon from './StopwatchIcon.vue';
import { formatSeconds } from './formatSeconds';

export default {
  components: {
    StopwatchIcon
  },

  props: {
    pdi: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      started: !!this.pdi.road_test_started_at,
      ended: !!this.pdi.road_test_ended_at,
      // The seconds the server reported when the road test was loaded, and the
      // time on this computer when we got them. While the test is running the
      // elapsed time is worked out from these and the current time, instead of
      // adding one every tick, as browsers slow the ticks down in background
      // tabs and on locked phones.
      reportedSeconds: this.pdi.road_test_seconds || 0,
      reportedAt: Date.now(),
      now: Date.now(),
      busy: false,
      ticker: null
    };
  },

  computed: {
    running() {
      return this.started && !this.ended;
    },

    seconds() {
      if (!this.running) {
        return this.reportedSeconds;
      }

      return (
        this.reportedSeconds + Math.floor((this.now - this.reportedAt) / 1000)
      );
    },

    display() {
      return formatSeconds(this.seconds);
    }
  },

  mounted() {
    document.addEventListener('visibilitychange', this.refresh);

    if (this.running) {
      this.startTicker();
    }
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.refresh);

    this.stopTicker();
  },

  methods: {
    start() {
      this.busy = true;

      window.Ajax.post(`pdi/${this.pdi.identifier}/road-test/start`)
        .then(response => {
          this.started = true;
          this.ended = false;
          this.report(response.data.road_test_seconds || 0);
          this.busy = false;

          this.startTicker();
        })
        .catch(error => this.failed(error, 'start'));
    },

    stop() {
      this.busy = true;

      window.Ajax.post(`pdi/${this.pdi.identifier}/road-test/stop`)
        .then(response => {
          this.stopTicker();

          // Read before ending, as the elapsed time stops being worked out
          const total = response.data.road_test_seconds || this.seconds;

          this.ended = true;
          this.reportedSeconds = total;
          this.busy = false;
        })
        .catch(error => this.failed(error, 'stop'));
    },

    failed(error, action) {
      this.busy = false;

      window.toastify.error(
        'Oops',
        error?.response?.data?.message ||
          `An error occurred while trying to ${action} the road test timer`
      );
    },

    report(seconds) {
      this.reportedSeconds = seconds;
      this.reportedAt = Date.now();
      this.now = this.reportedAt;
    },

    // Bring the time up to date straight away, rather than waiting for the
    // next tick, when the page becomes visible again.
    refresh() {
      this.now = Date.now();
    },

    startTicker() {
      this.stopTicker();

      this.ticker = setInterval(this.refresh, 1000);
    },

    stopTicker() {
      clearInterval(this.ticker);

      this.ticker = null;
    }
  }
};
</script>
